// src/pages/client/ClientReportsPage.jsx
// Reports landing page for client admins

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trackPageView, trackReportViewed } from '../../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || 'https://api.invenire.app';

export default function ClientReportsPage({ tenantKeyFromSubdomain }) {
  const navigate = useNavigate();
  const { tenantKey: tenantKeyFromUrl } = useParams();
  const tenantKey = tenantKeyFromSubdomain || tenantKeyFromUrl;
  
  const isSubdomain = Boolean(tenantKeyFromSubdomain);
  
  const [tenant, setTenant] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Generate correct paths based on routing mode
  const getPath = (path) => {
    if (isSubdomain) {
      return `/admin${path}`;
    } else {
      return `/client/${tenantKey}${path}`;
    }
  };

  const getBackPath = () => {
    if (isSubdomain) {
      return '/admin';
    } else {
      return `/client/${tenantKey}`;
    }
  };

  // Track page view
  useEffect(() => {
    const pagePath = isSubdomain ? '/admin/reports' : '/client/[key]/reports';
    trackPageView(pagePath, 'Client Reports');
    trackReportViewed('summary', null);
  }, [isSubdomain]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantKey]);

  const loadData = async () => {
    if (!tenantKey) return;
    
    try {
      // Load tenant info
      const tenantResponse = await fetch(`${API_BASE}/tenants/${tenantKey}`);
      if (tenantResponse.ok) {
        const tenantData = await tenantResponse.json();
        setTenant(tenantData);
        
        // Load jobs with stats for this tenant
        const summaryResponse = await fetch(`${API_BASE}/scoring/summary/tenant/${tenantData.id}`);
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setJobs(summaryData.jobs || []);
        }
      }
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Apply tenant branding (memoized to avoid dependency issues)
  const branding = useMemo(() => tenant?.branding || {}, [tenant?.branding]);
  const primaryColor = branding.primaryColor || "#3ECF8E";
  const companyName = branding.companyName || tenant?.name || tenantKey;
  
  // Get backgroundColor from branding
  const backgroundColor = branding.backgroundColor || branding.background_color || '#0d1117';
  
  // Calculate if background is light or dark
  const isLightBackground = useMemo(() => {
    const hex = backgroundColor.replace('#', '');
    if (hex.length !== 6) return false;
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  }, [backgroundColor]);
  
  // Dynamic styles based on tenant branding (matching ClientConfigPage exactly)
  const styles = useMemo(() => {
    const textPrimary = branding.textColor || (isLightBackground ? '#111827' : '#ffffff');
    const textSecondary = isLightBackground ? '#4b5563' : '#9ca3af';
    const cardBg = isLightBackground ? '#ffffff' : 'rgba(255,255,255,0.03)';
    const cardBorder = isLightBackground ? '#e5e7eb' : 'rgba(255,255,255,0.1)';
    
    return {
      container: {
        minHeight: '100vh',
        backgroundColor: backgroundColor,
        color: textPrimary,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      },
      header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 40px',
        borderBottom: `1px solid ${cardBorder}`,
      },
      title: {
        fontSize: 28,
        fontWeight: 700,
        color: primaryColor,
        margin: 0,
      },
      subtitle: {
        fontSize: 14,
        color: textSecondary,
        marginTop: 4,
      },
      backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 16px',
        background: isLightBackground ? '#f3f4f6' : 'rgba(255,255,255,0.1)',
        color: textPrimary,
        border: 'none',
        borderRadius: 6,
        fontSize: 14,
        cursor: 'pointer',
        textDecoration: 'none',
      },
      content: {
        maxWidth: 1000,
        margin: '0 auto',
        padding: '32px 40px',
      },
      section: {
        marginBottom: 48,
      },
      sectionTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 20,
      },
      card: {
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: 12,
        padding: 24,
      },
      cardTitle: {
        fontSize: 18,
        fontWeight: 600,
        marginBottom: 8,
        color: textPrimary,
      },
      cardText: {
        fontSize: 14,
        color: textSecondary,
        lineHeight: 1.5,
        marginBottom: 12,
      },
      grid2: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 24,
      },
      grid4: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 16,
      },
      jobsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 20,
      },
      statNumber: {
        fontSize: 32,
        fontWeight: 700,
        color: textPrimary,
      },
      statLabel: {
        fontSize: 12,
        color: textSecondary,
        marginTop: 4,
      },
      emptyState: {
        textAlign: 'center',
        padding: 48,
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: 12,
        color: textSecondary,
      },
      primaryButton: {
        marginTop: 16,
        background: primaryColor,
        color: '#ffffff',
        border: 'none',
        padding: '12px 24px',
        borderRadius: 6,
        cursor: 'pointer',
        fontWeight: 600,
      },
      outlineButton: {
        width: '100%',
        padding: '10px 16px',
        background: 'transparent',
        border: `1px solid ${primaryColor}`,
        borderRadius: 6,
        color: primaryColor,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
      },
      textPrimary,
      textSecondary,
      cardBg,
      cardBorder,
      primaryColor,
    };
  }, [branding, primaryColor, backgroundColor, isLightBackground]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: styles.textSecondary }}>
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header - consistent with all client pages */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{companyName}</h1>
          <p style={styles.subtitle}>Reports & Analytics</p>
        </div>
        <button onClick={() => navigate(getBackPath())} style={styles.backButton}>
          ← Back to Dashboard
        </button>
      </div>

      <div style={styles.content}>
        {/* Report Types */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Report Types</h2>
          <div style={styles.grid2}>
            <div style={{ ...styles.card, borderLeft: `3px solid ${primaryColor}` }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
              <h3 style={styles.cardTitle}>Job Reports</h3>
              <p style={styles.cardText}>
                View all candidates for a specific job, ranked by AI score with hiring recommendations.
              </p>
              <span style={{ fontSize: 13, color: primaryColor }}>Select a job below →</span>
            </div>
            
            <div style={styles.card}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>👤</div>
              <h3 style={styles.cardTitle}>Candidate Reports</h3>
              <p style={styles.cardText}>
                Detailed breakdown of individual interviews with Q&A analysis and video timestamps.
              </p>
              <span style={{ fontSize: 13, color: styles.textSecondary }}>Click on a candidate in job report →</span>
            </div>
          </div>
        </div>

        {/* Jobs with Reports */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Your Job Reports</h2>
          {jobs.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No jobs found. Load some jobs first to generate reports.</p>
              <button onClick={() => navigate(getPath('/jobs'))} style={styles.primaryButton}>
                Load Jobs
              </button>
            </div>
          ) : (
            <div style={styles.jobsGrid}>
              {jobs.map(job => (
                <div 
                  key={job.id} 
                  style={{ ...styles.card, cursor: 'pointer' }}
                  onClick={() => navigate(getPath(`/jobs/${job.id}/report`))}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: 20 }}>📋</span>
                    <span style={{ fontSize: 16, fontWeight: 600, color: styles.textPrimary }}>{job.title}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, fontSize: 13, color: styles.textSecondary, marginBottom: 12 }}>
                    <span>{job.interview_count || 0} candidates</span>
                    <span>•</span>
                    <span>{job.analyzed_count || 0} analyzed</span>
                  </div>
                  {job.top_score && (
                    <div style={{ fontSize: 13, color: styles.textSecondary, marginBottom: 16 }}>
                      Top Score: <span style={{ color: '#22c55e', fontWeight: 600 }}>{job.top_score.toFixed(1)}</span>
                    </div>
                  )}
                  <button style={styles.outlineButton}>
                    View Report →
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Overview</h2>
          <div style={styles.grid4}>
            <div style={{ ...styles.card, textAlign: 'center', padding: 20 }}>
              <div style={{ ...styles.statNumber, color: primaryColor }}>{jobs.length}</div>
              <div style={styles.statLabel}>Active Jobs</div>
            </div>
            <div style={{ ...styles.card, textAlign: 'center', padding: 20 }}>
              <div style={styles.statNumber}>
                {jobs.reduce((sum, j) => sum + (j.interview_count || 0), 0)}
              </div>
              <div style={styles.statLabel}>Total Interviews</div>
            </div>
            <div style={{ ...styles.card, textAlign: 'center', padding: 20 }}>
              <div style={styles.statNumber}>
                {jobs.reduce((sum, j) => sum + (j.analyzed_count || 0), 0)}
              </div>
              <div style={styles.statLabel}>Analyzed</div>
            </div>
            <div style={{ ...styles.card, textAlign: 'center', padding: 20 }}>
              <div style={styles.statNumber}>
                {(() => {
                  const jobsWithScores = jobs.filter(j => j.avg_score != null || j.top_score != null);
                  if (jobsWithScores.length === 0) return "—";
                  const avg = jobsWithScores.reduce((sum, j) => sum + (j.avg_score ?? j.top_score), 0) / jobsWithScores.length;
                  return avg.toFixed(1);
                })()}
              </div>
              <div style={styles.statLabel}>Avg Score</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
